import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'init' | 'setting-session' | 'ready' | 'submitting' | 'success' | 'error'>('init');
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    (async () => {
      setStatus('setting-session');
      setError(null);
      log.debug('[AUTH-CB] Starting authentication callback processing');

      let saved: any = null;
      try {
        const storedData = sessionStorage.getItem('reset_tokens');
        if (storedData) {
          saved = JSON.parse(storedData);
          log.debug('[AUTH-CB] Loaded from sessionStorage:', {
            has_access_token: !!saved?.access_token,
            has_refresh_token: !!saved?.refresh_token,
            has_token_hash: !!saved?.token_hash,
            has_token: !!saved?.token,
            type: saved?.type,
            timestamp: saved?.ts
          });
        }
      } catch (e) {
        log.error('[AUTH-CB] Failed to parse sessionStorage:', e);
      }

      const href = window.location.href;
      const rawQuery = window.location.search?.slice(1) || '';
      const rawHash = href.includes('#') ? href.split('#', 2)[1] : '';
      const urlParams = new URLSearchParams(rawQuery || rawHash);

      const token = (saved?.token) || urlParams.get('token') || '';
      const token_hash = (saved?.token_hash) || urlParams.get('token_hash') || token;
      let type = (saved?.type) || urlParams.get('type') || '';
      let access_token = (saved?.access_token) || urlParams.get('access_token') || '';
      let refresh_token = (saved?.refresh_token) || urlParams.get('refresh_token') || '';

      log.debug('[AUTH-CB] Token candidates:', {
        token: token ? `${token.substring(0, 10)}...` : 'none',
        token_hash: token_hash ? `${token_hash.substring(0, 10)}...` : 'none',
        access_token: access_token ? `${access_token.substring(0, 10)}...` : 'none',
        refresh_token: refresh_token ? `${refresh_token.substring(0, 10)}...` : 'none',
        type
      });

      try {
        log.debug('[AUTH-CB] Clearing any existing session');
        await supabase.auth.signOut();
      } catch (e) {
        log.warn('[AUTH-CB] Error during signOut (can be ignored):', e);
      }

      if (token_hash) {
        log.debug('[AUTH-CB] Attempting OTP verification with token_hash');
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash
          });

          if (error) {
            log.error('[AUTH-CB] verifyOtp failed:', error);
            throw error;
          }

          if (!data?.session?.user) {
            log.error('[AUTH-CB] verifyOtp succeeded but no user in response');
            throw new Error('No user session created from token verification');
          }

          log.debug('[AUTH-CB] verifyOtp SUCCESS - user authenticated:', data.session.user.id);
          setStatus('ready');
          return;
        } catch (e: any) {
          log.error('[AUTH-CB] verifyOtp error:', e);
          const errorMessage = e?.message || 'Failed to verify recovery token';
          if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
            setError(`${errorMessage}. Please request a new password reset link.`);
          } else {
            setError(errorMessage);
          }
          setStatus('error');
          return;
        }
      }

      if (access_token && refresh_token) {
        log.debug('[AUTH-CB] Attempting setSession with implicit tokens');
        try {
          const { data, error: setErr } = await supabase.auth.setSession({
            access_token,
            refresh_token
          });

          if (setErr) {
            log.error('[AUTH-CB] setSession failed:', setErr);
            throw setErr;
          }

          if (!data?.session?.user) {
            log.error('[AUTH-CB] setSession succeeded but no user in response');
            throw new Error('No user session created from tokens');
          }

          log.debug('[AUTH-CB] setSession SUCCESS - user authenticated:', data.session.user.id);
          setStatus('ready');
          return;
        } catch (e: any) {
          log.error('[AUTH-CB] setSession error:', e);
          const errorMessage = e?.message || 'Failed to establish session';
          if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
            setError(`${errorMessage}. Your recovery link may have expired. Please request a new password reset link.`);
          } else {
            setError(errorMessage);
          }
          setStatus('error');
          return;
        }
      }

      log.error('[AUTH-CB] No valid authentication tokens found in URL or sessionStorage');
      setError(
        'No valid authentication tokens found. Please ensure you copied the complete recovery link from your email, or request a new password reset link.'
      );
      setStatus('error');
    })();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setStatus('submitting');
    setError(null);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password });
      if (upErr) throw upErr;
      try { sessionStorage.removeItem('reset_tokens'); } catch {}
      setStatus('success');
      setTimeout(() => navigate('/'), 1200);
    } catch (e: any) {
      log.error('[AUTH-CB] updateUser error', e);
      setStatus('ready');
      setError(e?.message || 'Could not update password.');
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #f8fafc, #f1f5f9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '2rem',
        width: '100%',
        maxWidth: '400px'
      }}>
        {status === 'setting-session' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 1rem',
              border: '4px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <h1 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Verifying authentication</h1>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>Processing your recovery tokens...</p>
            <style dangerouslySetInnerHTML={{
              __html: '@keyframes spin { to { transform: rotate(360deg); } }'
            }} />
          </div>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>Set a new password</h1>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                New password
              </label>
              <input
                type="password"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                minLength={8}
                required
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                Confirm password
              </label>
              <input
                type="password"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
            {error && <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>{error}</p>}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#111827',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
                opacity: status === 'submitting' ? 0.5 : 1
              }}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Saving…' : 'Save password'}
            </button>
          </form>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#059669', margin: 0 }}>Password updated</h1>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>Redirecting…</p>
          </div>
        )}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1rem',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem'
            }}>
              ⚠️
            </div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#dc2626' }}>Authentication Failed</h1>
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#991b1b',
              textAlign: 'left'
            }}>
              <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Error Details:</strong>
              {error}
            </div>
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#f9fafb',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#6b7280',
              textAlign: 'left'
            }}>
              <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#374151' }}>What to do next:</strong>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li>Try the manual recovery helper below</li>
                <li>Request a new password reset link</li>
                <li>Contact support if the problem persists</li>
              </ul>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onClick={() => navigate('/auth/debug')}
                onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
              >
                Try Manual Recovery Helper
              </button>
              <button
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: '#111827',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onClick={() => navigate('/login')}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#111827'}
              >
                Go to Login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
